import { action, extendObservable } from 'mobx';
import { apiFetchGet, apiFetchPost } from '../helpers/api';
import {
    LIST_GROUP_TASKS,
    GET_COMPANY_TASKS_USERS,
    GET_COMPANY_TASKS_CATEGORIES,
    EDIT_TASK_STATUS,
    TASK_MINE,
    TASK_NOT_COMMENCED,
    TASK_NOT_COMMENCED_TXT,
    TASK_IN_PROGRESS,
    TASK_IN_PROGRESS_TXT,
    TASK_COMPLETE,
    TASK_COMPLETE_TXT,
    TASK_ARCHIVED,
    TASK_ARCHIVED_TXT,
    TITLE,
    CATEGORY_NAME,
    DUE_DATE_TIME,
    ASSIGNEE_NAME,
    STATUS,
} from '../helpers/constants';
import * as utils from '../helpers/utils';
import * as local from '../helpers/localStorage';
import store from './store';
import _ from 'lodash';
import moment from 'moment';

export default class TasksStore {
    constructor(factory) {
        this.factory = factory;

        extendObservable(this, {
            //#region LOCAL VARS

            data: {
                companies: {},
                users: {},
                categories: {},
            },
            dataFilteredByHierarchy: [],
            usersFilteredByHierarchy: [],
            categoriesFilteredByHierarchy: [],
            shouldDisplayTaskTab: true,
            init: true,
            loading: true,
            showMyTasksTab: true,
            currentFilterSelection: 0,
            currentColumnOrder: 'asc',

            // filters text box values
            titleSearchText: '',
            categorySearchText: '',
            dueDateSearchText: '',
            ownerSearchText: '',
            statusSearchText: '',

            dueStartDate: null,
            dueEndDate: null,
            noDueDate: false,

            set: action((field, value) => {
                this[field] = value;
            }),
            //#endregion

            //#region GET METHODS

            numberOfAllNewTasks: () => {
                let currentData = this.dataFilteredByHierarchy;
                let numberOfNewItems = 0;
                if (currentData !== 'undefined') {
                    currentData.forEach(task => {
                        if (task.IsNew) {
                            numberOfNewItems++;
                        }
                    });
                }

                return numberOfNewItems;
            },

            numberOfMyNewTasks: () => {
                let currentData = this.dataFilteredByHierarchy;
                let numberOfMyNewItems = 0;
                if (currentData !== 'undefined') {
                    currentData.forEach(task => {
                        if (task.IsMyTask && task.IsNew) {
                            numberOfMyNewItems++;
                        }
                    });
                }

                return numberOfMyNewItems;
            },

            isExistMyTask: () => {
                let currentData = this.dataFilteredByHierarchy;
                var flag = false;
                if (currentData !== 'undefined') {
                    for (var i = 0; i < currentData.length; i++) {
                        if (currentData[i].IsMyTask === 1) {
                            flag = true;
                            break;
                        }
                    }

                    return flag;
                } else {
                    return flag;
                }
            },

            //#endregion

            //#region SET METHODS

            setShowMyTasksTab: action(value => {
                this.showMyTasksTab = value;
            }),

            setShouldDisplayTaskTab: action('setShouldDisplayTaskTab', setShouldDisplayTaskTab => {
                this.shouldDisplayTaskTab = setShouldDisplayTaskTab;
            }),

            refreshShouldDisplayTaskTab: () => {
                this.setShouldDisplayTaskTab(this.filterTasksByCurrentHierarchy().length !== 0);
            },

            get noDueDateFilter() {
                return this.dueStartDate === null && this.dueEndDate === null && this.noDueDate === false
            },

            setTasks: action('setTasks', (instanceId, json) => {
                this.loading = true;
                const tasks = json.TaskList;
                const groups = {};

                tasks.forEach(task => {
                    task.InstanceId = instanceId;

                    if (this.isTaskViewed(task.InstanceId, task.GroupId, task.TaskId)) {
                        task.IsNew = false;
                    } else {
                        task.IsNew = true;
                    }

                    if (!groups.hasOwnProperty(task.GroupId)) {
                        groups[task.GroupId] = { [task.TaskId]: task };
                    } else {
                        groups[task.GroupId][task.TaskId] = task;
                    }
                });

                this.data.companies[instanceId] = groups;

                this.loading = false;
                this.refreshShouldDisplayTaskTab();
                if (this.init) {
                    this.factory.groups.setVisualIndicatorsToGroupsAndCompanies();
                    this.init = false;
                }
            }),

            setTaskUsers: action('setTaskUsers', (instanceId, json) => {
                const users = json.UserList;
                const groups = {};

                users.forEach(user => {
                    user.InstanceId = instanceId;

                    if (!groups.hasOwnProperty(user.GroupId)) {
                        groups[user.GroupId] = { [user.UserId]: user };
                    } else {
                        groups[user.GroupId][user.UserId] = user;
                    }
                });

                this.data.users[instanceId] = groups;
            }),

            setTaskCategories: action('setTaskCategories', (instanceId, json) => {
                const categories = json.CategoryList;
                const groups = {};

                categories.forEach(category => {
                    category.InstanceId = instanceId;

                    if (!groups.hasOwnProperty(category.GroupId)) {
                        groups[category.GroupId] = { [category.TaskCategoryId]: category };
                    } else {
                        groups[category.GroupId][category.TaskCategoryId] = category;
                    }
                });

                this.data.categories[instanceId] = groups;
            }),

            editTaskStatus: async (instanceId, taskId, statusId) => {
                let params = {
                    instanceId,
                    taskId,
                    statusId,
                };

                await apiFetchPost(EDIT_TASK_STATUS, this.factory.user.token, params);
                this.fetch()
                this.filterTasksByCurrentHierarchy()
                this.refreshShouldDisplayTaskTab()
                
            },

            setLocalDataTaskStatus: async (instanceId, groupId, taskId, statusId) => {
                var statusText = this.getStatusText(statusId);
                this.setTaskPropertiesInFullDataSet(
                    instanceId,
                    groupId,
                    taskId,
                    2,
                    statusId,
                    statusText,
                );
                this.setTaskPropertiesInFilteredData(
                    instanceId,
                    groupId,
                    taskId,
                    2,
                    statusId,
                    statusText,
                );
            },

            setCurrentFilteredDataIntoTempArray(currentArray) {
                var tempArray = [];
                for (var i = 0; i < currentArray.length; i++) {
                    tempArray[i] = currentArray[i];
                }

                return tempArray;
            },

            clearAllFilters() {
                this.set('titleSearchText', '');
                this.set('categorySearchText', '');
                this.set('dueDateSearchText', '');
                this.set('ownerSearchText', '');
                this.set('statusSearchText', '');

                this.set('dueStartDate', null);
                this.set('dueEndDate', null);
                this.set('noDueDate', false);
            },

            //#endregion

            //#region FILTER METHODS

            //#region main filter method
            filterTasksByCurrentHierarchy: action(() => {
                const tasksFilteredByHierarchy = [];
                const usersFilteredByHierarchy = [];
                const categoriesFilteredByHierarchy = [];

                const selectedCompany = this.factory.companies.selectedId;
                const selectedGroup = this.factory.groups.selectedId;
                const companies = this.data.companies;

                if (selectedCompany === null) {
                    for (const key in companies) {
                        if (companies.hasOwnProperty(key)) {
                            tasksFilteredByHierarchy.push(...this.tasksFilteredByCompany(key));
                            usersFilteredByHierarchy.push(...this.usersFilteredByCompany(key));
                            categoriesFilteredByHierarchy.push(
                                ...this.categoriesFilteredByCompany(key),
                            );
                        }
                    }
                } else if (selectedGroup === null) {
                    tasksFilteredByHierarchy.push(...this.tasksFilteredByCompany(selectedCompany));
                    usersFilteredByHierarchy.push(...this.usersFilteredByCompany(selectedCompany));
                    categoriesFilteredByHierarchy.push(
                        ...this.categoriesFilteredByCompany(selectedCompany),
                    );
                } else {
                    tasksFilteredByHierarchy.push(
                        ...this.tasksFilteredByGroup(selectedCompany, selectedGroup),
                    );
                    usersFilteredByHierarchy.push(
                        ...this.usersFilteredByGroup(selectedCompany, selectedGroup),
                    );
                    categoriesFilteredByHierarchy.push(
                        ...this.categoriesFilteredByGroup(selectedCompany, selectedGroup),
                    );
                }

                const noArchivedTasks = tasksFilteredByHierarchy.filter(task => {
                    
                    
                    return task.StatusId !== "6" && task.StatusId !== 6
                })

                
                this.dataFilteredByHierarchy = noArchivedTasks;
                this.usersFilteredByHierarchy = usersFilteredByHierarchy;
                this.categoriesFilteredByHierarchy = categoriesFilteredByHierarchy;

                if (this.isExistMyTask()) {
                    this.setShowMyTasksTab(true);
                } else {
                    this.setShowMyTasksTab(false);
                }

                this.orderDataFilteredByHierarchy();

                return noArchivedTasks;
            }),
            //#endregion

            //#region tasks filtered methods
            tasksFilteredByCompany: companyId => {
                const tasksFilteredByCompany = [];
                const company = this.data.companies[companyId];
                for (const key in company) {
                    if (company.hasOwnProperty(key)) {
                        if (company[key] != null) {
                            tasksFilteredByCompany.push(...Object.values(company[key]));
                        }
                    }
                }

                return tasksFilteredByCompany;
            },

            tasksFilteredByGroup: (companyId, groupId) => {
                const group = _.get(this.data.companies, `${companyId}.${groupId}`, null);
                const tasksFilteredByGroup = [];

                if (group != null) {
                    tasksFilteredByGroup.push(...Object.values(group));
                }

                return tasksFilteredByGroup;
            },
            //#endregion

            //#region users filtered methods
            usersFilteredByCompany: companyId => {
                const usersFilteredByCompany = [];

                const company = this.data.users[companyId];
                for (const key in company) {
                    if (company.hasOwnProperty(key)) {
                        if (company[key] != null) {
                            usersFilteredByCompany.push(...Object.values(company[key]));
                        }
                    }
                }

                return this.removeDuplicates(usersFilteredByCompany, 'UserId');
            },

            usersFilteredByGroup: (companyId, groupId) => {
                const group = _.get(this.data.users, `${companyId}.${groupId}`, null);
                var usersFilteredByGroup = [];

                if (group != null) {
                    usersFilteredByGroup.push(...Object.values(group));
                }

                return this.removeDuplicates(usersFilteredByGroup, 'UserId');
            },
            //#endregion

            //#region categories filtered methods
            categoriesFilteredByCompany: companyId => {
                const categoriesFilteredByCompany = [];
                const company = this.data.categories[companyId];
                for (const key in company) {
                    if (company.hasOwnProperty(key)) {
                        if (company[key] != null) {
                            categoriesFilteredByCompany.push(...Object.values(company[key]));
                        }
                    }
                }

                return this.removeDuplicates(categoriesFilteredByCompany, 'TaskCategoryId');
            },

            categoriesFilteredByGroup: (companyId, groupId) => {
                const group = _.get(this.data.categories, `${companyId}.${groupId}`, null);
                const categoriesFilteredByGroup = [];

                if (group != null) {
                    categoriesFilteredByGroup.push(...Object.values(group));
                }

                return this.removeDuplicates(categoriesFilteredByGroup, 'TaskCategoryId');
            },
            //#endregion

            //#region TASK_MINE or TASK_ALL filtered method
            filterTasksByCurrentHierarchyAndFilter: filter => {
                if (filter === TASK_MINE) {
                    return this.dataFilteredByHierarchy.filter(task => {
                        return task.IsMyTask === filter;
                    });
                }

                
                return this.dataFilteredByHierarchy;
            },
            //#endregion

            //#endregion

            //#region FETCH TASK DATA

            fetch: () => {
                const companyList = this.factory.companies.data.Companies;
                return this.fetchTasksFromCompanies(companyList);
            },

            fetchTaskUsers: () => {
                const companyList = this.factory.companies.data.Companies;
                return this.fetchTaskUsersFromCompanies(companyList);
            },

            fetchTaskCategories: () => {
                const companyList = this.factory.companies.data.Companies;
                return this.fetchTaskCategoriesFromCompanies(companyList);
            },

            fetchTasksFromCompanies: companyList => {
                if (typeof companyList !== 'undefined') {
                    return Promise.all(
                        companyList.map(company => {
                            if (company) {
                                /* return apiFetchGet(GET_COMPANY_TASKS, this.factory.user.token, {
                                    instanceId: company.InstanceId,
                                }).then(json => {
                                    if (json !== null) {
                                        this.setTasks(company.InstanceId, json);
                                    }
                                }); */

                                return this.fetchTasksFromCompanyGroups(this.factory.user.token, company).then(json => {
                                    this.setTasks(company.InstanceId, json);
                                    return json;                                    
                                 });
                            } else {
                                return true;
                            }
                        }),
                    );
                }
            },

            fetchTasksFromCompanyGroups: async (token, company) => {
                var tasksJson = {};
                await Promise.all(
                    company.Groups.map(async (item, y) => {
                        if (item) {

                            const json = await apiFetchGet(LIST_GROUP_TASKS, token, {
                                    instanceId: company.InstanceId,
                                    groupId:    item.GroupId
                                }).then(json => {
                                    return json;
                                });

                             if(!tasksJson.hasOwnProperty('TaskList')) {
                                tasksJson = Object.assign(tasksJson, json);
                             } else {
                                 let tmpTasks = tasksJson.TaskList;
                                 var attributes = Object.keys(json.TaskList)
                                 for (var i = 0; i < attributes.length; i++) {
                                    tmpTasks.push(json.TaskList[attributes[i]]);
                                }
                                tasksJson.TaskList = tmpTasks;
                            }

                            //console.log(company.InstanceId, item.GroupId, {tasksJson});
                            return tasksJson;
                        } else {
                            return {};
                        }
                    })
                ).then(res => {
                    return tasksJson;
                });
                return tasksJson;
            },

            fetchTaskUsersFromCompanies: companyList => {
                if (typeof companyList !== 'undefined') {
                    return Promise.all(
                        companyList.map(company => {
                            if (company) {
                                return apiFetchGet(
                                    GET_COMPANY_TASKS_USERS,
                                    this.factory.user.token,
                                    {
                                        instanceId: company.InstanceId,
                                    },
                                ).then(json => {
                                    if (json !== null) {
                                        this.setTaskUsers(company.InstanceId, json);
                                    }
                                });
                            } else {
                                return true;
                            }
                        }),
                    );
                }
            },

            fetchTaskCategoriesFromCompanies: companyList => {
                if (typeof companyList !== 'undefined') {
                    return Promise.all(
                        companyList.map(company => {
                            if (company) {
                                return apiFetchGet(
                                    GET_COMPANY_TASKS_CATEGORIES,
                                    this.factory.user.token,
                                    {
                                        instanceId: company.InstanceId,
                                    },
                                ).then(json => {
                                    if (json !== null) {
                                        this.setTaskCategories(company.InstanceId, json);
                                    }
                                });
                            } else {
                                return true;
                            }
                        }),
                    );
                }
            },

            //#endregion

            //#region BADGES & LOCAL STORE FUNCTIONS
            markTaskAsViewed: (instanceId, groupId, taskId) => {
                const key = local.LS_TASKS + instanceId + groupId + store.user.profile[0].UserId;

                this.setTaskPropertiesInFullDataSet(instanceId, groupId, taskId, 1, 0, '');
                this.setTaskPropertiesInFilteredData(instanceId, groupId, taskId, 1, 0, '');
                local.saveNumberByKeyStringInLocalStorageArray(key, taskId);
                store.groups.resetVisualIndicatorsForGroup(instanceId, groupId);
            },

            isTaskViewed: (instanceId, groupId, taskId) => {
                const key = local.LS_TASKS + instanceId + groupId + store.user.profile[0].UserId;

                return local.isKeyExists(key, taskId);
            },

            setTaskPropertiesInFilteredData: action(
                (companyId, groupId, taskId, flag, valueNum, valueText) => {
                    let currentData = this.dataFilteredByHierarchy;
                    if (currentData !== 'undefined') {
                        currentData.forEach(task => {
                            if (
                                task.InstanceId === companyId &&
                                task.GroupId === groupId &&
                                task.TaskId === taskId
                            ) {
                                if (flag === 1) {
                                    task.IsNew = false;
                                }

                                if (flag === 2) {
                                    // if the task if overdue and status is changed to
                                    // complete/archived, set task overdue to false
                                    if (task.Overdue && valueNum > 2) {
                                        task.Overdue = false;
                                    }

                                    // if task is not overdue and it is changed
                                    // from complete/archive to Not commenced/In progress
                                    // check if the due date is overdue; if YES - set task overdue to true
                                    if (!task.Overdue && task.StatusId > 2 && valueNum < 4) {
                                        // check DueDateTime for overdue
                                        if (
                                            utils.compareDateString(
                                                task.DueDateTime,
                                                utils.getNowInUtc(),
                                            )
                                        ) {
                                            task.Overdue = true;
                                        }
                                    }

                                    task.StatusId = valueNum;
                                    task.Status = valueText;
                                }
                            }
                        });
                    }
                },
            ),

            setTaskPropertiesInFullDataSet: (
                companyId,
                groupId,
                taskId,
                flag,
                valueNum,
                valueText,
            ) => {
                let data = this.data;
                if (data !== 'undefined') {
                    for (const key in data.companies) {
                        if (data.companies.hasOwnProperty(key)) {
                            if (key === companyId.toString()) {
                                for (const keyGroup in data.companies[key]) {
                                    if (data.companies[key].hasOwnProperty(keyGroup)) {
                                        if (keyGroup === groupId.toString()) {
                                            for (const keyTask in data.companies[key][keyGroup]) {
                                                if (
                                                    data.companies[key][keyGroup].hasOwnProperty(
                                                        keyTask,
                                                    )
                                                ) {
                                                    if (keyTask === taskId.toString()) {
                                                        if (flag === 1) {
                                                            data.companies[key][keyGroup][
                                                                keyTask
                                                            ].IsNew = false;
                                                        }

                                                        if (flag === 2) {
                                                            // if the task if overdue and status is changed to
                                                            // complete/archived, set task overdue to false
                                                            if (
                                                                data.companies[key][keyGroup][
                                                                    keyTask
                                                                ].Overdue &&
                                                                valueNum > 2
                                                            ) {
                                                                data.companies[key][keyGroup][
                                                                    keyTask
                                                                ].Overdue = false;
                                                            }

                                                            // if task is not overdue and it is changed
                                                            // from complete/archive to Not commenced/In progress
                                                            // check if the due date is overdue; if YES - set task overdue to true
                                                            if (
                                                                !data.companies[key][keyGroup][
                                                                    keyTask
                                                                ].Overdue &&
                                                                data.companies[key][keyGroup][
                                                                    keyTask
                                                                ].StatusId > 2 &&
                                                                valueNum < 4
                                                            ) {
                                                                // check DueDateTime for overdue
                                                                if (
                                                                    utils.compareDateString(
                                                                        data.companies[key][
                                                                            keyGroup
                                                                        ][keyTask].DueDateTime,
                                                                        utils.getNowInUtc(),
                                                                    )
                                                                ) {
                                                                    data.companies[key][keyGroup][
                                                                        keyTask
                                                                    ].Overdue = true;
                                                                }
                                                            }

                                                            data.companies[key][keyGroup][
                                                                keyTask
                                                            ].StatusId = valueNum;
                                                            data.companies[key][keyGroup][
                                                                keyTask
                                                            ].Status = valueText;
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },

            //#endregion

            //#region ORDERING AND SEARCHING

            orderDataFilteredByHierarchy: action(() => {
                var columnName = '';
                switch (this.currentFilterSelection) {
                    case 1:
                        columnName = CATEGORY_NAME;
                        break;
                    case 2:
                        columnName = DUE_DATE_TIME;
                        break;
                    case 3:
                        columnName = ASSIGNEE_NAME;
                        break;
                    case 4:
                        columnName = STATUS;
                        break;
                    default:
                        columnName = TITLE;
                        break;
                }

                var tempArray = this.setCurrentFilteredDataIntoTempArray(
                    this.dataFilteredByHierarchy,
                );

                if (this.currentColumnOrder === 'asc') {
                    tempArray.sort(this.sortOnAsc(columnName));
                } else {
                    tempArray.sort(this.sortOnDesc(columnName));
                }

                this.dataFilteredByHierarchy.replace(tempArray);
            }),

            searchTasks: action(() => {
                var allArray = this.filterTasksByCurrentHierarchy();
                var tempArray = this.setCurrentFilteredDataIntoTempArray(allArray);

                //#region TITLE search
                if (this.titleSearchText !== '' && this.titleSearchText !== ' ') {
                    let searchText = this.titleSearchText.toLowerCase();
                    tempArray = tempArray.filter(t => t.Title !== null);
                    tempArray = tempArray.filter(t => t.Title.toLowerCase().includes(searchText));
                } else if (this.titleSearchText === ' ') {
                    tempArray = tempArray.filter(t => t.Title === null || t.Title === '');
                }

                //#endregion

                //#region CATEGORY search
                if (this.categorySearchText !== '' && this.categorySearchText !== ' ') {
                    let searchText = this.categorySearchText.toLowerCase();
                    tempArray = tempArray.filter(t => t.CategoryName !== null);
                    tempArray = tempArray.filter(t =>
                        t.CategoryName.toLowerCase().includes(searchText),
                    );
                } else if (this.categorySearchText === ' ') {
                    tempArray = tempArray.filter(
                        t => t.CategoryName === null || t.CategoryName === '',
                    );
                }
                //#endregion

                //#region DUEDATE search
                if (this.noDueDate) {
                    tempArray = tempArray.filter(t => t.DueDateTime === '0001-01-01T00:00:00');
                } else {
                    if (this.dueStartDate !== null && this.dueEndDate !== null) {
                        let startDate = this.dueStartDate._d;
                        let endDate = this.dueEndDate._d;
                        tempArray = tempArray.filter(t => t.DueDateTime !== '0001-01-01T00:00:00');
                        tempArray = tempArray.filter(
                            t =>
                                (moment.utc(t.DueDateTime).valueOf() >=
                                    moment.utc(startDate).valueOf()) &
                                (moment.utc(t.DueDateTime).valueOf() <=
                                    moment.utc(endDate).valueOf()),
                        );
                    } else if (this.dueStartDate !== null) {
                        let startDate = this.dueStartDate._d;
                        tempArray = tempArray.filter(t => t.DueDateTime !== '0001-01-01T00:00:00');
                        tempArray = tempArray.filter(
                            t =>
                                moment.utc(t.DueDateTime).valueOf() >=
                                moment.utc(startDate).valueOf(),
                        );
                    } else if (this.dueEndDate !== null) {
                        let endDate = this.dueEndDate._d;
                        tempArray = tempArray.filter(t => t.DueDateTime !== '0001-01-01T00:00:00');
                        tempArray = tempArray.filter(
                            t =>
                                moment.utc(t.DueDateTime).valueOf() <=
                                moment.utc(endDate).valueOf(),
                        );
                    }
                }
                //#endregion

                //#region OWNER search
                if (this.ownerSearchText !== '' && this.ownerSearchText !== ' ') {
                    let searchText = this.ownerSearchText.toLowerCase();
                    tempArray = tempArray.filter(t => t.AssigneeName !== null);
                    tempArray = tempArray.filter(t =>
                        t.AssigneeName.toLowerCase().includes(searchText),
                    );
                } else if (this.ownerSearchText === ' ') {
                    tempArray = tempArray.filter(
                        t => t.AssigneeName === null || t.AssigneeName === '',
                    );
                }
                //#endregion

                //#region STATUS search
                if (this.statusSearchText !== '') {
                    let statusArr = this.statusSearchText.split(',');

                    if (statusArr.length === 1) {
                        tempArray = tempArray.filter(t =>
                            t.Status.toLowerCase().includes(statusArr[0].toLowerCase()),
                        );
                    } else {
                        var arrayOfArr = [];
                        for (var i = 0; i < statusArr.length; i++) {
                            arrayOfArr[i] = tempArray.filter(t =>
                                t.Status.toLowerCase().includes(statusArr[i].toLowerCase()),
                            );
                        }

                        var mergedArray = [];
                        for (var x = 0; x < arrayOfArr.length; x++) {
                            var singleArr = arrayOfArr[x];
                            for (var a = 0; a < singleArr.length; a++) {
                                mergedArray.push(singleArr[a]);
                            }
                        }

                        tempArray = mergedArray;
                    }
                }
                //#endregion

                this.dataFilteredByHierarchy.replace(tempArray);
                this.orderDataFilteredByHierarchy();
            }),

            sortOnAsc(property) {
                return function (a, b) {
                    if (a[property] === null) {
                        a[property] = '';
                    }
                    if (b[property] === null) {
                        b[property] = '';
                    }
                    if (a[property].toLowerCase() < b[property].toLowerCase()) {
                        return -1;
                    } else if (a[property].toLowerCase() > b[property].toLowerCase()) {
                        return 1;
                    } else {
                        return 0;
                    }
                };
            },

            sortOnDesc(property) {
                return function (a, b) {
                    if (a[property] === null) {
                        a[property] = '';
                    }
                    if (b[property] === null) {
                        b[property] = '';
                    }
                    if (a[property].toLowerCase() > b[property].toLowerCase()) {
                        return -1;
                    } else if (a[property].toLowerCase() < b[property].toLowerCase()) {
                        return 1;
                    } else {
                        return 0;
                    }
                };
            },

            //#endregion

            //#region MISC FUNCS
            getStatusText: statusId => {
                var statusText = '';

                switch (Number(statusId)) {
                    case TASK_NOT_COMMENCED:
                        statusText = TASK_NOT_COMMENCED_TXT;
                        break;

                    case TASK_IN_PROGRESS:
                        statusText = TASK_IN_PROGRESS_TXT;
                        break;

                    case TASK_COMPLETE:
                        statusText = TASK_COMPLETE_TXT;
                        break;

                    case TASK_ARCHIVED:
                        statusText = TASK_ARCHIVED_TXT;
                        break;

                    default:
                        statusText = TASK_NOT_COMMENCED_TXT;
                        break;
                }

                return statusText;
            },

            removeDuplicates: (myArr, prop) => {
                return myArr.filter((obj, pos, arr) => {
                    return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos;
                });
            },

            //#endregion PRIVATE
        });
    }
}



// WEBPACK FOOTER //
// ./src/stores/TasksStore.js